import React from "react";
import { Box, HStack } from "@chakra-ui/react";
import type { LucideProps } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CloudRain, Zap, X } from "lucide-react";
import { SA_COLORS } from "./sensor-alert-colors";

/**
 * Tab strip for switching between Chuva (rain) and Raio (lightning) sub-blocks
 * inside the legacy `SensorAlertSection`.
 *
 * Spec 043 §FR-001 / §FR-006 / §FR-007:
 *   - Renders only tabs whose slice is configured (or available for configuration).
 *   - Visual matches Figma node 3581:31091 — bottom border on the strip + 1.5px
 *     primary underline on the active tab; icon shown only on the active tab.
 *   - When `onRemove` is provided, an X affordance appears beside each tab label
 *     so the user can drop one slice without nuking the whole alert (the parent
 *     funnels the click into a confirmation modal — same pattern as Aurora).
 */
export type SensorAlertTabType = "rain" | "lightning";

export interface SensorAlertTabsProps {
  activeTab: SensorAlertTabType;
  onTabChange: (tab: SensorAlertTabType) => void;
  hasRain: boolean;
  hasLightning: boolean;
  /** Optional badge with the rain sensor count rendered next to the Chuva label. */
  rainSensorCount?: number;
  /** Per-tab remove affordance. Click delegates to parent for confirmation. */
  onRemove?: (tab: SensorAlertTabType) => void;
  /**
   * Tabs que devem ser ocultados independente do estado das slices. O parent
   * decide a regra (ex.: gate de origem na Spec 043). Convenção espelha o
   * `disabledTypes` do `SensorAlertTabs` da aurora-ui.
   */
  hiddenTabs?: ReadonlyArray<SensorAlertTabType>;
}

const REMOVE_LABEL_KEY: Record<SensorAlertTabType, string> = {
  rain: "removeRainAlert",
  lightning: "removeLightningAlert",
};

/**
 * Coerces the requested active tab to one that is currently visible — used
 * by both the section body (to avoid rendering an empty pane when the
 * configured-tab condition flips) and by the gateway entry (to land the map
 * on the same slice the user came from). Centralised here so the two
 * callsites can't drift.
 */
export function coerceSensorAlertTab(
  activeTab: SensorAlertTabType,
  hasRain: boolean,
  hasLightning: boolean,
): SensorAlertTabType {
  if (activeTab === "rain" && !hasRain && hasLightning) return "lightning";
  if (activeTab === "lightning" && !hasLightning && hasRain) return "rain";
  return activeTab;
}

const ORDER: SensorAlertTabType[] = ["rain", "lightning"];

const TAB_ICONS: Record<
  SensorAlertTabType,
  React.ComponentType<LucideProps>
> = {
  rain: CloudRain,
  lightning: Zap,
};

const LABEL_KEY: Record<SensorAlertTabType, string> = {
  rain: "sensorTypeRainTab",
  lightning: "sensorTypeLightningTab",
};

export const SensorAlertTabs: React.FC<SensorAlertTabsProps> = ({
  activeTab,
  onTabChange,
  hasRain,
  hasLightning,
  rainSensorCount,
  onRemove,
  hiddenTabs,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.sensorAlert",
  });

  const visibleTabs = ORDER.filter((type) => {
    if (hiddenTabs?.includes(type)) return false;
    return type === "rain" ? hasRain : hasLightning;
  });

  if (visibleTabs.length === 0) return null;

  return (
    <HStack
      spacing={0}
      h="36px"
      borderBottom="1px solid"
      borderBottomColor={SA_COLORS.tabStripBorder}
      align="stretch"
    >
      {visibleTabs.map((tab) => {
        const isActive = tab === activeTab;
        const Icon = TAB_ICONS[tab];

        // The active underline lives on the tab button itself so a hover
        // background on the tab and the underline share the same bounding
        // box. Pull it 1px below the strip's own border so the 1.5px accent
        // visually replaces the strip border instead of stacking.
        const tabButton = (
          <Box
            as="button"
            type="button"
            onClick={() => onTabChange(tab)}
            display="flex"
            alignItems="center"
            gap="10px"
            px="12px"
            py="4px"
            bg="transparent"
            borderBottom="1.5px solid"
            borderBottomColor={isActive ? SA_COLORS.primary : "transparent"}
            marginBottom="-1px"
            cursor="pointer"
            _hover={isActive ? undefined : { bg: "blackAlpha.50" }}
          >
            {isActive && (
              <Box as={Icon} boxSize="16px" color={SA_COLORS.primary} />
            )}
            <Box
              as="span"
              fontSize="14px"
              fontWeight={isActive ? 500 : 400}
              lineHeight="20px"
              color={isActive ? SA_COLORS.primary : SA_COLORS.textTabInactive}
              whiteSpace="nowrap"
            >
              {t(LABEL_KEY[tab])}
            </Box>
            {tab === "rain" &&
              typeof rainSensorCount === "number" &&
              rainSensorCount > 0 && (
                <Box
                  as="span"
                  fontSize="12px"
                  bg={isActive ? SA_COLORS.primaryCounterBg : "gray.100"}
                  color={
                    isActive ? SA_COLORS.primary : SA_COLORS.textTabInactive
                  }
                  px="8px"
                  py="0px"
                  borderRadius="full"
                  lineHeight="18px"
                >
                  {rainSensorCount}
                </Box>
              )}
          </Box>
        );

        // Without `onRemove` we render the bare tab button — same DOM as
        // before this prop existed. With `onRemove` the X is a sibling
        // button (avoids nested <button> and lets the X own its click).
        if (!onRemove) return <React.Fragment key={tab}>{tabButton}</React.Fragment>;

        return (
          <HStack key={tab} spacing="6px" align="center" pr="6px">
            {tabButton}
            <Box
              as="button"
              type="button"
              aria-label={t(REMOVE_LABEL_KEY[tab]) ?? undefined}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onRemove(tab);
              }}
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              w="16px"
              h="16px"
              borderRadius="4px"
              color={SA_COLORS.textTabInactive}
              cursor="pointer"
              _hover={{ bg: "blackAlpha.100", color: SA_COLORS.primary }}
            >
              <X size={14} />
            </Box>
          </HStack>
        );
      })}
    </HStack>
  );
};
